import { useState, type ReactNode } from 'react';
import { AnimatePresence } from 'motion/react';
import type { TierBadge } from '../../../../modules/api';
import { DisclaimerModal } from '../../../../modules/disclaimer';
import { GlassCard } from '../../shared/GlassCard';
import { TierCard } from './TierCard';

export interface PackInfoCardProps {
  title: string;
  buybackPercent?: number;
  /** The sibling packs of the active category — one card per slide. */
  tiers: TierBadge[];
  /**
   * Operator-overridable pack description from the backend. When present it
   * replaces the default buyback subtitle; `null`/empty keeps the default copy.
   */
  description?: string | null;
  /** Fires with the clicked offer id; the parent focuses the slider on it. */
  onTierSelect?: (poolOfferId: number) => void;
  /** The Max Pull block, closing the card (Figma 19074:254247). */
  children?: ReactNode;
}

/**
 * The first glass card of the desktop sidebar (Figma 19074:254247): title,
 * buyback line, the category's packs and the Max Pull block. Desktop only —
 * the phone shows the title under the slider instead.
 */
export function PackInfoCard({
  title,
  buybackPercent = 90,
  tiers,
  description = null,
  onTierSelect,
  children,
}: PackInfoCardProps) {
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  return (
    <>
      <div data-co-component="PackInfoCard" className="hidden @[800px]:block">
        <GlassCard className="flex flex-col gap-4">
          <div className="flex flex-col items-center gap-3 text-center">
            {/* Room for two lines always, so a short title doesn't move the
                cards below it while swiping (repackz-handoff §8.1). */}
            <h2
              className="m-0 flex min-h-[2lh] items-center font-extrabold uppercase leading-[1.05] tracking-[-0.03em]"
              style={{
                color: 'var(--co-PackInfoCard-titleColor, #ffffff)',
                fontFamily: 'var(--co-PackInfoCard-fontFamily), Inter, system-ui, sans-serif',
                fontSize: 'var(--co-PackInfoCard-titleSize, 28px)',
              }}
            >
              {title || 'Pack title'}
            </h2>
            <p
              className="m-0 max-w-[360px] leading-[23px] tracking-[-0.1px]"
              style={{
                color: 'var(--co-PackInfoCard-subtitleColor, rgba(255,255,255,0.8))',
                fontFamily: 'var(--co-PackInfoCard-fontFamily), Inter, system-ui, sans-serif',
                fontSize: 'var(--co-PackInfoCard-subtitleSize, 15px)',
              }}
            >
              {description ? (
                description
              ) : (
                <>
                  <span className="font-semibold">{buybackPercent}% FMV buyback</span> available — or hold
                  and redeem your vaulted graded card.
                </>
              )}{' '}
              <button
                type="button"
                onClick={() => setDisclaimerOpen(true)}
                className="cursor-pointer underline underline-offset-2 transition-opacity hover:opacity-80"
                style={{ color: 'inherit' }}
              >
                Disclaimer
              </button>
            </p>
          </div>

          {/* A lone card would only repeat the pack in view, so the row
              shows from two packs up (as the site's storefront does). */}
          {tiers.length > 1 && (
            <div className="scrollbar-hide flex gap-2 overflow-x-auto">
              {tiers.map((tier) => (
                <TierCard
                  key={tier.poolOfferId}
                  badge={tier}
                  {...(onTierSelect ? { onClick: () => onTierSelect(tier.poolOfferId) } : {})}
                />
              ))}
            </div>
          )}

          {children}
        </GlassCard>
      </div>

      <AnimatePresence>
        {disclaimerOpen && <DisclaimerModal onClose={() => setDisclaimerOpen(false)} />}
      </AnimatePresence>
    </>
  );
}
